import styled, { css, keyframes } from 'styled-components'
import { Button } from '../../atoms/Button'

export const AIContainer = styled.div`
  padding: ${({ theme }) => theme.spacing.large};
  display: flex;
  flex-direction: column;
  height: 100%;
`

export const AIPromptContainer = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
`

export const AIPromptHeader = styled.div`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing.medium};
  margin-bottom: ${({ theme }) => theme.spacing.medium};
  color: ${({ theme }) => theme.colors.primary};
`

export const AIPromptTitle = styled.h3`
  font-size: 16px;
  font-weight: 600;
  color: ${({ theme }) => theme.colors.text.primary};
  margin: 0;
`

export const AIPromptDescription = styled.p`
  font-size: 13px;
  color: ${({ theme }) => theme.colors.text.secondary};
  margin-bottom: ${({ theme }) => theme.spacing.large};
  line-height: 1.5;
`

export const AIExamplesContainer = styled.div`
  margin-bottom: ${({ theme }) => theme.spacing.large};
`

export const ExamplesTitle = styled.p`
  font-size: 11px;
  font-weight: 500;
  color: ${({ theme }) => theme.colors.text.tertiary};
  text-transform: uppercase;
  letter-spacing: 0.5px;
  margin-bottom: ${({ theme }) => theme.spacing.small};
`

export const ExampleChips = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing.small};
`

export const ExampleChip = styled.button`
  padding: ${({ theme }) => theme.spacing.small}
    ${({ theme }) => theme.spacing.medium};
  background: ${({ theme }) => theme.colors.background.tertiary};
  border: 1px solid ${({ theme }) => theme.colors.border.default};
  border-radius: ${({ theme }) => theme.borderRadius.medium};
  font-size: 12px;
  color: ${({ theme }) => theme.colors.text.secondary};
  cursor: pointer;
  transition: all ${({ theme }) => theme.transitions.default};
  text-align: left;
  font-family: inherit;
  line-height: 1.4;

  &:hover {
    background: ${({ theme }) => theme.colors.primary}10;
    border-color: ${({ theme }) => theme.colors.primary};
    color: ${({ theme }) => theme.colors.primary};
  }
`

export const AIPromptInputWrapper = styled.div`
  position: relative;
  margin-bottom: ${({ theme }) => theme.spacing.large};
`

export const AIPromptTextArea = styled.textarea`
  width: 100%;
  padding: ${({ theme }) => theme.spacing.medium};
  padding-bottom: 52px;
  background: ${({ theme }) => theme.colors.background.tertiary};
  border: 1px solid ${({ theme }) => theme.colors.border.default};
  border-radius: ${({ theme }) => theme.borderRadius.medium};
  font-size: 14px;
  font-family: inherit;
  color: ${({ theme }) => theme.colors.text.primary};
  resize: none;
  transition: all ${({ theme }) => theme.transitions.default};
  line-height: 1.5;

  &:focus {
    outline: none;
    background: ${({ theme }) => theme.colors.background.primary};
    border-color: ${({ theme }) => theme.colors.primary};
    box-shadow: 0 0 0 3px ${({ theme }) => theme.colors.primary}1A;
  }

  &::placeholder {
    color: ${({ theme }) => theme.colors.text.tertiary};
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
`

export const AIPromptActions = styled.div`
  position: absolute;
  bottom: ${({ theme }) => theme.spacing.small};
  right: ${({ theme }) => theme.spacing.small};
  display: flex;
  gap: ${({ theme }) => theme.spacing.small};
`

const pulseAnimation = keyframes`
  0% {
    box-shadow: 0 0 0 0 rgba(255, 59, 48, 0.4);
  }
  70% {
    box-shadow: 0 0 0 8px rgba(255, 59, 48, 0);
  }
  100% {
    box-shadow: 0 0 0 0 rgba(255, 59, 48, 0);
  }
`

export const VoiceButton = styled.button<{ $recording?: boolean }>`
  width: 32px;
  height: 32px;
  border: none;
  background: ${({ theme }) => theme.colors.background.primary};
  color: ${({ theme }) => theme.colors.text.secondary};
  border-radius: ${({ theme }) => theme.borderRadius.small};
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all ${({ theme }) => theme.transitions.default};
  box-shadow: ${({ theme }) => theme.shadows.sm};

  &:hover {
    background: ${({ theme }) => theme.colors.background.primary};
    color: ${({ theme }) => theme.colors.primary};
    box-shadow: ${({ theme }) => theme.shadows.md};
  }

  ${({ $recording, theme }) =>
    $recording &&
    css`
      background: ${theme.colors.danger};
      color: white;
      animation: ${pulseAnimation} 1.5s infinite;
    `}
`

export const SubmitButton = styled(Button)`
  padding: 6px 16px;
  font-size: 13px;
  height: 32px;
`

// AI Thinking Animation
const thinkingAnimation = keyframes`
  0%, 80%, 100% {
    transform: scale(0);
    opacity: 0.5;
  }
  40% {
    transform: scale(1);
    opacity: 1;
  }
`

export const AIThinkingContainer = styled.div`
  background: ${({ theme }) => theme.colors.background.tertiary};
  border-radius: ${({ theme }) => theme.borderRadius.medium};
  padding: ${({ theme }) => theme.spacing.large};
  text-align: center;
`

export const ThinkingAnimation = styled.div`
  display: flex;
  justify-content: center;
  gap: ${({ theme }) => theme.spacing.small};
  margin-bottom: ${({ theme }) => theme.spacing.medium};
`

export const ThinkingDot = styled.div`
  width: 8px;
  height: 8px;
  background: ${({ theme }) => theme.colors.primary};
  border-radius: 50%;
  animation: ${thinkingAnimation} 1.4s infinite ease-in-out both;

  &:nth-child(1) {
    animation-delay: -0.32s;
  }

  &:nth-child(2) {
    animation-delay: -0.16s;
  }
`

export const ThinkingText = styled.p`
  font-size: 13px;
  color: ${({ theme }) => theme.colors.text.secondary};
  margin: 0;
`

// AI Response
const slideUpAnimation = keyframes`
  from {
    opacity: 0;
    transform: translateY(10px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`

export const AIResponseContainer = styled.div`
  background: ${({ theme }) => theme.colors.primary}10;
  border: 1px solid ${({ theme }) => theme.colors.primary};
  border-radius: ${({ theme }) => theme.borderRadius.medium};
  padding: ${({ theme }) => theme.spacing.medium};
  animation: ${slideUpAnimation} 0.3s ease-out;
  margin-bottom: ${({ theme }) => theme.spacing.medium};
`

export const ResponseHeader = styled.div`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing.small};
  color: ${({ theme }) => theme.colors.primary};
  font-size: 13px;
  font-weight: 500;
  margin-bottom: ${({ theme }) => theme.spacing.medium};
`

export const ResponseContent = styled.div`
  font-size: 12px;
  color: ${({ theme }) => theme.colors.text.primary};
  line-height: 1.5;

  p {
    margin: 0 0 ${({ theme }) => theme.spacing.small} 0;
  }

  ul {
    margin: ${({ theme }) => theme.spacing.small} 0 0;
    padding-left: 20px;
  }

  li {
    margin-bottom: 4px;
  }
`

// Response History
export const ResponseHistoryContainer = styled.div`
  margin-top: ${({ theme }) => theme.spacing.large};
  padding-top: ${({ theme }) => theme.spacing.large};
  border-top: 1px solid ${({ theme }) => theme.colors.border.light};
`

export const ResponseHistoryTitle = styled.h4`
  font-size: 14px;
  font-weight: 600;
  margin: 0 0 ${({ theme }) => theme.spacing.medium} 0;
  color: ${({ theme }) => theme.colors.text.primary};
`

export const ResponseItem = styled.div`
  padding: ${({ theme }) => theme.spacing.medium};
  background: ${({ theme }) => theme.colors.background.secondary};
  border-radius: ${({ theme }) => theme.borderRadius.medium};
  margin-bottom: ${({ theme }) => theme.spacing.medium};
  cursor: pointer;
  transition: all ${({ theme }) => theme.transitions.default};

  &:hover {
    background: ${({ theme }) => theme.colors.background.tertiary};
  }
`

export const ResponsePrompt = styled.div`
  font-size: 13px;
  font-weight: 500;
  color: ${({ theme }) => theme.colors.text.primary};
  margin-bottom: 4px;
`

export const ResponseExplanation = styled.div`
  font-size: 12px;
  color: ${({ theme }) => theme.colors.text.secondary};
  line-height: 1.5;
`

// Error Message
export const ErrorMessage = styled.div`
  padding: ${({ theme }) => theme.spacing.medium};
  background: ${({ theme }) => theme.colors.danger}1A;
  border: 1px solid ${({ theme }) => theme.colors.danger}4D;
  border-radius: ${({ theme }) => theme.borderRadius.small};
  color: ${({ theme }) => theme.colors.danger};
  font-size: 13px;
  margin-bottom: ${({ theme }) => theme.spacing.medium};
`
